import { useEffect, useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { changePassword } from '../../../api-client/identity'
import { APIErrors } from '../../../api-client/enums'
import useValidation from '../../../hooks/use-validation'
import { Button } from '../../../components/button'
import { FormField } from '../../../components/inputs'

const re = {
  newPassword: /^(?=^.{8,}$)((?!.*\s)(?=.*[a-z]))((?=(.*\d){1,})|(?=(.*\W){1,}))^.*$/i,
}

const validationErrors = {
  newPassword: "Password doesn't meet strength requirements",
  confirmPassword: "Passwords doesn't match",
}

export default function UpdatePassword({ onClose, onSuccess }) {
  const [state, setState] = useState({
    password: '',
    newPassword: '',
    confirmPassword: '',
  })
  const [mappedServerErrors, setMappedServerErrors] = useState({})

  const [showErrors, { isFormValid, errors }] = useValidation(state, validate)

  const { mutate: mutatePassword, isLoading, isSuccess, error } = useMutation({
    mutationFn: changePassword,
    onSuccess,
  })

  useEffect(() => {
    if (error?.errors) setMappedServerErrors(mapServerErrors(error.errors))
  }, [error?.errors])

  const handleSubmit = (e) => {
    e.preventDefault()

    if (!isFormValid) {
      showErrors()
    } else {
      mutatePassword({
        currentPassword: state.password,
        newPassword: state.newPassword,
        confirmNewPassword: state.confirmPassword,
      })
    }
  }

  const handleInputChange = (e) => {
    e.persist()

    setState((prev) => ({ ...prev, [e.target.name]: e.target.value }))
  }

  useEffect(() => {
    if (isSuccess) onClose()
  }, [isSuccess, onClose])

  return (
    <form className='wrapper' onSubmit={handleSubmit}>
      <h1 className='title'>Update your password</h1>
      <div className='inputFieldWrapper currentPassword'>
        {(errors?.password || mappedServerErrors.currentPassword) && (
          <label htmlFor='password' className='errorLabel'>
            {errors.password || mappedServerErrors.currentPassword}
          </label>
        )}
        <FormField
          type='password'
          id='password'
          name='password'
          error={errors?.password || mappedServerErrors.currentPassword}
          placeholder='Current Password'
          autoComplete='current-password'
          value={state.password}
          onChange={handleInputChange}
        />
      </div>
      <div className='inputFieldWrapper newPassword'>
        {errors?.newPassword && (
          <label htmlFor='newPassword' className='errorLabel'>
            {errors.newPassword}
          </label>
        )}

        <FormField
          type='password'
          id='newPassword'
          name='newPassword'
          error={errors?.newPassword}
          placeholder='New Password'
          autoComplete='new-password'
          onChange={handleInputChange}
        />
      </div>
      <div className='inputFieldWrapper confirmPassword'>
        {errors?.confirmPassword && (
          <label htmlFor='confirmPassword' className='errorLabel'>
            {errors.confirmPassword}
          </label>
        )}

        <FormField
          type='password'
          id='confirmPassword'
          name='confirmPassword'
          error={errors?.confirmPassword}
          placeholder='Confirm New Password'
          autoComplete='new-password'
          onChange={handleInputChange}
        />
      </div>

      <p className='passwordHint'>
        Password must be a minimum of 8 characters, contain a letter AND a number OR special
        character.
      </p>

      <Button className='saveBtn' type='submit' disabled={isLoading}>
        Save
      </Button>

      <style jsx>{`
        .wrapper {
          position: relative;
          width: 43.6rem;
          padding: 2.6rem 1.8rem 4.1rem;
          background: #fff;
          border-radius: 1rem;

          :global(.saveBtn) {
            display: block;
            margin: 0 auto;
          }
        }

        .inputFieldWrapper {
          &.currentPassword {
            margin: 0 0 3rem;
          }

          &.newPassword {
            margin: 0 0 1.7rem;
          }

          &.confirmPassword {
            margin: 0 0 1rem;
          }
        }

        .title {
          margin: 0 0 2.8rem;
          text-align: center;
          font-size: 3.2rem;
        }

        .passwordHint {
          max-width: 32rem;
          margin: 0 auto 2.8rem;
          text-align: center;
          font-style: italic;
          font-size: 1.4rem;
        }

        .inputFieldWrapper {
          position: relative;
        }

        .errorLabel {
          display: block;
          margin: 0 0 1rem;
          color: var(--alert-color);
          font-size: 1.4rem;

          &.error {
            color: var(--alert-color);
          }
        }

        @media (max-width: 1024px) {
          .wrapper {
            width: 100%;
            height: 100%;
            padding: 2.7rem 1.4rem 2.3rem;

            :global(.saveBtn) {
              position: fixed;
              bottom: 2.1rem;
              left: 1.2rem;
              width: calc(100vw - 2.4rem);
            }
          }

          .title {
            margin: 0 0 3rem;
            text-align: center;
            font-size: 1.8rem;
          }
        }
      `}</style>
    </form>
  )
}

function validate(state) {
  const errors = {}
  let isValid = true

  for (const field of Object.keys(state)) {
    if (state[field] === '') {
      isValid = false
      errors[field] = 'Required'
    }

    if (re[field]) {
      if (!re[field].test(state[field])) {
        isValid = false
        errors[field] = validationErrors[field]
      }
    }

    if (field === 'confirmPassword' && state.newPassword !== state.confirmPassword) {
      isValid = false
      errors[field] = validationErrors[field]
    }
  }

  return { isValid, errors }
}

function mapServerErrors(errors) {
  const mappedErrors = {}

  errors.forEach((error) => {
    switch (+error?.code) {
      case APIErrors.IncorrectPassword:
        mappedErrors['currentPassword'] = 'Password is incorrect'
        break
      default:
        mappedErrors[APIErrors.Unknown] = error.message
        break
    }
  })

  return mappedErrors
}
